import React from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "./Header";
import Footer from "./Footer";
import config from "../../config";

const CHANNELS = {
  shopify: {
    id: "1567898605",
    name: "Shopify",
    image: "assets/img/shopify.png",
    description:
      "Sync orders, customers and shipment information with Shopify.",
    type: "E-commerce",
    connectUrl: "/shopify_connect",
  },

  woocommerce: {
    id: "1507808901",
    name: "WooCommerce",
    image: "assets/img/woo.png",
    description:
      "Connect your online store and automatically manage shipments.",
    type: "E-commerce",
    connectUrl: "/channel_integrate?id=1507808901",
  },
};

const SHOPIFY_REDIRECT_URI =
  "https://skyshipbackendelite.fniix.com/auth/shopify/callback";

const SHOPIFY_SCOPES =
  "write_assigned_fulfillment_orders, read_assigned_fulfillment_orders, write_draft_orders, read_draft_orders, write_fulfillments, read_fulfillments, write_merchant_managed_fulfillment_orders, read_merchant_managed_fulfillment_orders, write_inventory, read_inventory, write_orders, read_orders, write_products, read_products, write_product_listings, read_product_listings, write_product_feeds, read_product_feeds";

const Channel_list = () => {
  const [connectedStores, setConnectedStores] = React.useState([]);

  const [loading, setLoading] = React.useState(true);

  const [expandedStore, setExpandedStore] = React.useState(null);

  const [updatingStoreId, setUpdatingStoreId] = React.useState(null);

  const { pathname, search } = useLocation();

  const navigate = useNavigate();

  /*
   * =========================================================
   * TOAST
   * =========================================================
   */

  const Toast = React.useMemo(
    () =>
      Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
      }),
    []
  );

  /*
   * =========================================================
   * GET CONNECTED CHANNELS
   * =========================================================
   */

  const getChannelList = React.useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${config.apiUrl}/user/channel_list`,
        {},
        {
          headers: config.headers,
        }
      );

      if (response.data?.status === true) {
        setConnectedStores(
          Array.isArray(response.data.output) ? response.data.output : []
        );
      } else {
        setConnectedStores([]);
      }
    } catch (error) {
      console.error("channel_list error =>", error);

      setConnectedStores([]);

      Toast.fire({
        icon: "error",
        title: "Unable to load channels",
      });
    } finally {
      setLoading(false);
    }
  }, [Toast]);

  /*
   * =========================================================
   * INITIAL LOAD
   * =========================================================
   */

  React.useEffect(() => {
    getChannelList();
  }, [getChannelList]);

  /*
   * =========================================================
   * SHOPIFY / OAUTH RESULT
   * =========================================================
   */

  React.useEffect(() => {
    const params = new URLSearchParams(search);

    const status = params.get("status");

    const oauth = params.get("oauth");

    const reason = params.get("reason");

    if (!status && !oauth) {
      return;
    }

    const showResult = async () => {
      try {
        if (status === "success") {
          await Swal.fire(
            "Store Integrated",
            "Your Shopify store was added successfully!",
            "success"
          );
        } else if (status === "failed") {
          await Swal.fire(
            "Integration Failed",
            "Something went wrong during installation.",
            "error"
          );
        } else if (status === "already_connected") {
          await Swal.fire(
            "Store already connected",
            "This Shopify store is already linked to another account.",
            "error"
          );
        }

        if (oauth === "success") {
          await Swal.fire(
            "Credentials Updated",
            "Shopify credentials were updated successfully.",
            "success"
          );
        } else if (oauth === "failed") {
          await Swal.fire(
            reason || "Integration Failed",
            "Something went wrong during the integration.",
            "error"
          );
        }
      } finally {
        navigate(pathname, {
          replace: true,
        });

        getChannelList();
      }
    };

    showResult();
  }, [search, pathname, navigate, getChannelList]);

  /*
   * =========================================================
   * UPDATE ACTIVE / INACTIVE STATUS
   * =========================================================
   *
   * status 1 = Active
   * status 0 = Inactive
   * status 5 = Pending
   *
   * Only status 5 disables the toggle.
   * =========================================================
   */

  const updateStatus = async (storeId, status) => {
    if (!storeId) {
      return;
    }

    try {
      setUpdatingStoreId(storeId);

      const response = await axios.post(
        `${config.apiUrl}/user/update_store_status`,
        {
          status: Number(status),
          store_id: storeId,
        },
        {
          headers: config.headers,
        }
      );

      if (response.data?.status === true) {
        setConnectedStores((prev) =>
          prev.map((store) =>
            String(store?.store_id) === String(storeId)
              ? {
                  ...store,
                  status: Number(status),
                }
              : store
          )
        );

        Toast.fire({
          icon: "success",
          title:
            Number(status) === 1
              ? "Store activated successfully"
              : "Store deactivated successfully",
        });
      } else {
        throw new Error(response.data?.message || "Unable to update status.");
      }
    } catch (error) {
      console.error("update_store_status error =>", error);

      Toast.fire({
        icon: "error",
        title:
          error?.response?.data?.message || "Unable to update store status",
      });
    } finally {
      setUpdatingStoreId(null);
    }
  };

  /*
   * =========================================================
   * INLINE SHOPIFY FIELD UPDATE
   * =========================================================
   */

  const updateStoreField = (storeId, key, value) => {
    setConnectedStores((prev) =>
      prev.map((store) =>
        String(store?.store_id) === String(storeId)
          ? {
              ...store,
              [key]: value,
            }
          : store
      )
    );
  };

  /*
   * =========================================================
   * START SHOPIFY OAUTH
   * =========================================================
   *
   * This follows the existing Channel_list.jsx flow:
   *
   * 1. save client credentials
   * 2. redirect to Shopify OAuth
   * =========================================================
   */

  const startShopifyOAuth = async (storeId, shop, clientId, clientSecret) => {
    const cleanShop = String(shop || "").trim();

    const cleanClientId = String(clientId || "").trim();

    const cleanClientSecret = String(clientSecret || "").trim();

    if (!cleanShop || !cleanClientId || !cleanClientSecret) {
      Swal.fire("Error", "Shop, Client ID & Client Secret required", "error");

      return;
    }

    try {
      const response = await axios.post(
        `${config.apiUrl}/user/save_shopify_oauth_creds`,
        {
          store_id: storeId,
          client_id: cleanClientId,
          client_secret: cleanClientSecret,
          shop: cleanShop,
        },
        {
          headers: config.headers,
        }
      );

      if (response.data?.status !== true) {
        throw new Error(
          response.data?.message || "Unable to save Shopify credentials."
        );
      }

      const redirectUri = encodeURIComponent(SHOPIFY_REDIRECT_URI);

      const scopes = encodeURIComponent(SHOPIFY_SCOPES);

      const state = encodeURIComponent(String(storeId));

      const shopDomain = cleanShop
        .replace(/^https?:\/\//i, "")
        .replace(/\/+$/, "");

      const url =
        `https://${shopDomain}/admin/oauth/authorize` +
        `?client_id=${encodeURIComponent(cleanClientId)}` +
        `&scope=${scopes}` +
        `&redirect_uri=${redirectUri}` +
        `&state=${state}`;

      window.location.href = url;
    } catch (error) {
      console.error("save_shopify_oauth_creds error =>", error);

      Toast.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to start Shopify integration",
      });
    }
  };

  /*
   * =========================================================
   * CHANNEL HELPERS
   * =========================================================
   */

  const getChannelConfig = (channelId) => {
    const id = String(channelId || "");

    if (id === CHANNELS.shopify.id) {
      return CHANNELS.shopify;
    }

    if (id === CHANNELS.woocommerce.id) {
      return CHANNELS.woocommerce;
    }

    return null;
  };

  const isShopify = (store) =>
    String(store?.channel_id || "") === CHANNELS.shopify.id;

  const isWooCommerce = (store) =>
    String(store?.channel_id || "") === CHANNELS.woocommerce.id;

  /*
   * =========================================================
   * STORE STATUS
   * =========================================================
   */

  const getStoreStatus = (store) => {
    const status = Number(store?.status);

    if (status === 1) {
      return {
        label: "Active",
        className: "on",
        active: true,
        pending: false,
      };
    }

    if (status === 5) {
      return {
        label: "Pending",
        className: "paused",
        active: false,
        pending: true,
      };
    }

    return {
      label: "Inactive",
      className: "paused",
      active: false,
      pending: false,
    };
  };

  /*
   * =========================================================
   * STORE CARD STATE
   * =========================================================
   */

  const isChannelConnected = (channelId) => {
    return connectedStores.some(
      (store) => String(store?.channel_id) === String(channelId)
    );
  };

  /*
   * =========================================================
   * SHOPIFY HELP TOGGLE
   * =========================================================
   */

  const toggleShopifyDetails = (storeId) => {
    setExpandedStore((prev) =>
      String(prev) === String(storeId) ? null : storeId
    );
  };

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <div>
      <Header>
        <div className="content">
          {/* =================================================
              PAGE HEADER
              ================================================= */}

          <div className="ol-header ig-top">
            <div className="ol-hd">
              <button
                type="button"
                className="ol-back"
                title="Go back"
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-arrow-left"></i>
              </button>
            </div>

            <div className="ol-actions">
              <a href="/developer_apis#api-keys" className="ol-btn ghost">
                <i className="bi bi-key"></i>
                Get API Key
              </a>

              <a href="/developer_apis" className="ol-btn">
                <i className="bi bi-book"></i>
                API Docs
              </a>
            </div>
          </div>

          {/* =================================================
              HERO
              ================================================= */}

          <div className="ig-hero">
            <span className="ig-eyebrow">
              <i className="bi bi-grid-1x2"></i>
              Integrations
            </span>

            <h1 className="ig-title">Connect your store</h1>

            <p className="ig-desc">
              Connect Shopify or WooCommerce to sync orders and manage shipments
              from one place.
            </p>
          </div>

          {/* =================================================
              CONNECTED CHANNELS
              ================================================= */}

          <div className="ig-conn-wrap">
            <div className="ig-conn-head">
              <div>
                <h2 className="ig-conn-title">Connected channels</h2>

                <p className="ig-conn-sub">
                  Manage your connected stores, workspaces and channels
                </p>
              </div>

              <span className="ig-conn-count">
                {connectedStores.length} connection
                {connectedStores.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="ig-conn-list">
              {loading ? (
                <div className="ig-conn-empty">
                  <i className="bi bi-arrow-repeat"></i>
                  Loading connections...
                </div>
              ) : connectedStores.length === 0 ? (
                <div className="ig-conn-empty">
                  <i className="bi bi-plug"></i>
                  No channels connected yet. Connect one to get started.
                </div>
              ) : (
                connectedStores.map((item) => {
                  const channel = getChannelConfig(item?.channel_id);

                  if (!channel) {
                    return null;
                  }

                  const shopify = isShopify(item);

                  const status = getStoreStatus(item);

                  const isUpdating =
                    String(updatingStoreId) === String(item?.store_id);

                  return (
                    <React.Fragment key={item?.store_id}>
                      {/* =================================
                            CONNECTED ROW
                            ================================= */}

                      <div
                        className={`ig-conn-row ${status.active ? "" : "off"}`}
                        data-conn={item?.store_id}
                      >
                        {/* LOGO */}

                        <span
                          className={`ig-logo ${
                            shopify ? "ig-logo-sh" : "ig-logo-woo"
                          }`}
                        >
                          <img src={channel.image} alt={channel.name} />
                        </span>

                        {/* STORE INFO */}

                        <div className="ig-conn-info">
                          <b>{item?.store_name || "Unnamed Store"}</b>

                          <span>
                            {channel.name}

                            {item?.store_url ? ` · ${item.store_url}` : ""}
                          </span>

                          <span>Store ID: #{item?.store_id}</span>

                          {shopify && (
                            <span>Webhook URL: {item?.webhook_url || ""}</span>
                          )}
                        </div>

                        {/* STATUS */}

                        <span className={`ig-st ${status.className}`}>
                          <i className="bi bi-circle-fill"></i> {status.label}
                        </span>

                        {/* TOGGLE */}

                        <label
                          className="ig-conn-toggle form-check form-switch"
                          title={
                            status.pending
                              ? "Store is pending"
                              : "Toggle active / inactive"
                          }
                        >
                          <input
                            type="checkbox"
                            role="switch"
                            className="form-check-input"
                            checked={status.active}
                            disabled={status.pending || isUpdating}
                            onChange={(e) =>
                              updateStatus(
                                item.store_id,
                                e.target.checked ? 1 : 0
                              )
                            }
                          />
                        </label>

                        {/* SHOPIFY SETUP */}

                        {shopify && (
                          <span className="ig-conn-acts">
                            <button
                              type="button"
                              className="ig-mini"
                              onClick={() =>
                                toggleShopifyDetails(item.store_id)
                              }
                            >
                              <i
                                className={`bi ${
                                  String(expandedStore) ===
                                  String(item.store_id)
                                    ? "bi-chevron-up"
                                    : "bi-chevron-down"
                                }`}
                              ></i>
                              Setup
                            </button>
                          </span>
                        )}
                      </div>

                      {/* =================================
                            SHOPIFY SETUP DETAILS
                            ================================= */}

                      {shopify &&
                        String(expandedStore) === String(item.store_id) && (
                          <div className="ig-shopify-details">
                            {/* WEBHOOK SETUP */}

                            <div className="ig-shopify-help">
                              <h3>Create Webhook (Mandatory)</h3>

                              <p>
                                After installing the app, create webhooks in
                                Shopify using the URL below.
                              </p>

                              <ul>
                                <li>Login into Shopify Admin panel.</li>

                                <li>
                                  Open{" "}
                                  <a
                                    href="https://admin.shopify.com"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    https://admin.shopify.com
                                  </a>
                                </li>

                                <li>
                                  In the Shopify Admin panel, go to{" "}
                                  <strong>Settings</strong>.
                                </li>

                                <li>
                                  Click on <strong>Notifications</strong> from
                                  the left-hand menu.
                                </li>

                                <li>
                                  Scroll down to the <strong>Webhooks</strong>{" "}
                                  section and click on{" "}
                                  <strong>Create Webhook</strong>.
                                </li>

                                <li>
                                  In the popup:
                                  <ul>
                                    <li>
                                      Select the <strong>Event</strong>{" "}
                                      <em>Order Creation</em> to sync Shopify
                                      orders to the panel.
                                    </li>

                                    <li>
                                      Choose the <strong>Format</strong> (JSON
                                      is recommended).
                                    </li>

                                    <li>
                                      In the URL field, paste:
                                      <code className="ig-webhook-code">
                                        {item?.webhook_url || ""}
                                      </code>
                                    </li>

                                    <li>
                                      Click <strong>Save</strong>.
                                    </li>

                                    <li
                                      style={{
                                        marginTop: 10,
                                      }}
                                    >
                                      Again, click{" "}
                                      <strong>Create Webhook</strong>.
                                    </li>

                                    <li>
                                      Select the <strong>Event</strong>{" "}
                                      <em>Order Update</em> to update Shopify
                                      orders in the panel.
                                    </li>

                                    <li>
                                      Choose the <strong>Format</strong> (JSON
                                      is recommended).
                                    </li>

                                    <li>
                                      In the URL field, paste:
                                      <code className="ig-webhook-code">
                                        https://skyshipbackendelite.fniix.com/webhook/shopify_update/
                                        {item?.store_id}
                                      </code>
                                    </li>

                                    <li>
                                      Click <strong>Save</strong>.
                                    </li>
                                  </ul>
                                </li>

                                <li>Your webhooks are now active.</li>
                              </ul>
                            </div>

                            {/* APP CREDENTIALS */}

                            {(!item?.store_api_key ||
                              item.store_api_key === "") && (
                              <div className="ig-app-credentials">
                                <h5>App Credentials</h5>

                                <p>
                                  This is a one-time setup and is required for:
                                </p>

                                <ul>
                                  <li>
                                    Auto update tracking numbers in Shopify
                                  </li>

                                  <li>
                                    Mark orders as fulfilled automatically
                                  </li>
                                </ul>

                                <p>
                                  To continue, please fill in your{" "}
                                  <b>Store URL</b>, <b>Client ID</b>, and{" "}
                                  <b>Client Secret</b>.
                                </p>

                                <p>
                                  We have already shown your <b>Store URL</b>{" "}
                                  above. Please fill that Store URL.
                                </p>

                                <p>
                                  To get your <b>Client ID</b> and{" "}
                                  <b>Client Secret</b>:
                                </p>

                                <ol>
                                  <li>Go to Shopify Partners Dashboard.</li>

                                  <li>
                                    Click on <b>App distribution</b> →{" "}
                                    <b>All apps</b>
                                  </li>

                                  <li>
                                    Click on <b>View on Dev Dashboard</b> for
                                    your app.
                                  </li>

                                  <li>
                                    Go to <b>Settings</b> of that app.
                                  </li>

                                  <li>
                                    You will see your <b>Client ID</b> and{" "}
                                    <b>Client Secret</b> there.
                                  </li>
                                </ol>

                                {/* STORE URL */}

                                <input
                                  type="text"
                                  className="form-control mb-2"
                                  placeholder="Shopify Store URL (example.myshopify.com)"
                                  value={item?.shop_input || ""}
                                  onChange={(e) =>
                                    updateStoreField(
                                      item.store_id,
                                      "shop_input",
                                      e.target.value
                                    )
                                  }
                                />

                                {/* CLIENT ID */}

                                <input
                                  type="text"
                                  className="form-control mb-2"
                                  placeholder="Client ID"
                                  value={item?.client_id_input || ""}
                                  onChange={(e) =>
                                    updateStoreField(
                                      item.store_id,
                                      "client_id_input",
                                      e.target.value
                                    )
                                  }
                                />

                                {/* CLIENT SECRET */}

                                <input
                                  type="password"
                                  className="form-control mb-2"
                                  placeholder="Client Secret"
                                  value={item?.client_secret_input || ""}
                                  onChange={(e) =>
                                    updateStoreField(
                                      item.store_id,
                                      "client_secret_input",
                                      e.target.value
                                    )
                                  }
                                />

                                {/* SUBMIT */}

                                <button
                                  type="button"
                                  className="btn btn-sm btn-success"
                                  onClick={() =>
                                    startShopifyOAuth(
                                      item.store_id,
                                      item.shop_input,
                                      item.client_id_input,
                                      item.client_secret_input
                                    )
                                  }
                                >
                                  Submit
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                    </React.Fragment>
                  );
                })
              )}
            </div>
          </div>

          {/* =================================================
              AVAILABLE INTEGRATIONS
              ================================================= */}

          <div className="ig-conn-head mt-4">
            <div>
              <h2 className="ig-conn-title">Available integrations</h2>

              <p className="ig-conn-sub">
                Connect your store in a few simple steps.
              </p>
            </div>
          </div>

          <div className="ig-grid">
            {/* =================================================
                SHOPIFY
                ================================================= */}

            <article className="card-base ig-card" data-ig="Shopify">
              <div className="ig-card-top">
                <span className="ig-logo ig-logo-sh">
                  <img src={CHANNELS.shopify.image} alt="Shopify" />
                </span>

                <span
                  className={`ig-state ${
                    isChannelConnected(CHANNELS.shopify.id) ? "on" : ""
                  }`}
                >
                  {isChannelConnected(CHANNELS.shopify.id) ? (
                    <>
                      <i className="bi bi-check-circle-fill"></i> Connected
                    </>
                  ) : (
                    <>
                      <span className="ig-dot"></span>
                      Not connected
                    </>
                  )}
                </span>
              </div>

              <h3 className="ig-name">Shopify</h3>

              <p className="ig-desc">{CHANNELS.shopify.description}</p>

              <div className="ig-card-foot">
                <span className="ig-tag">E-commerce</span>

                <button
                  type="button"
                  className={`ig-conn ${
                    isChannelConnected(CHANNELS.shopify.id) ? "on" : ""
                  }`}
                  onClick={() => navigate(CHANNELS.shopify.connectUrl)}
                >
                  <i
                    className={`bi ${
                      isChannelConnected(CHANNELS.shopify.id)
                        ? "bi-sliders"
                        : "bi-plug"
                    }`}
                  ></i>

                  {isChannelConnected(CHANNELS.shopify.id)
                    ? "Manage"
                    : "Connect"}
                </button>
              </div>
            </article>

            {/* =================================================
                WOOCOMMERCE
                ================================================= */}

            <article className="card-base ig-card" data-ig="WooCommerce">
              <div className="ig-card-top">
                <span className="ig-logo ig-logo-woo">
                  <img src={CHANNELS.woocommerce.image} alt="WooCommerce" />
                </span>

                <span
                  className={`ig-state ${
                    isChannelConnected(CHANNELS.woocommerce.id) ? "on" : ""
                  }`}
                >
                  {isChannelConnected(CHANNELS.woocommerce.id) ? (
                    <>
                      <i className="bi bi-check-circle-fill"></i> Connected
                    </>
                  ) : (
                    <>
                      <span className="ig-dot"></span>
                      Not connected
                    </>
                  )}
                </span>
              </div>

              <h3 className="ig-name">WooCommerce</h3>

              <p className="ig-desc">{CHANNELS.woocommerce.description}</p>

              <div className="ig-card-foot">
                <span className="ig-tag">E-commerce</span>

                <button
                  type="button"
                  className={`ig-conn ${
                    isChannelConnected(CHANNELS.woocommerce.id) ? "on" : ""
                  }`}
                  onClick={() => navigate(CHANNELS.woocommerce.connectUrl)}
                >
                  <i
                    className={`bi ${
                      isChannelConnected(CHANNELS.woocommerce.id)
                        ? "bi-sliders"
                        : "bi-plug"
                    }`}
                  ></i>

                  {isChannelConnected(CHANNELS.woocommerce.id)
                    ? "Manage"
                    : "Connect"}
                </button>
              </div>
            </article>
          </div>
        </div>
      </Header>

      <Footer />
    </div>
  );
};

export default Channel_list;
